/**
 * The radar scope: one 2D canvas, north up, centred on the airport, nautical miles.
 *
 * Static layer (rings, airspace, video map, runways with extended centrelines, fixes,
 * scale markers) is drawn into an offscreen canvas and only redrawn when the view or the
 * realism options change; the aircraft layer is drawn every frame on top. The scope draws
 * only what is in AircraftState; it holds no sim rules.
 */
import { createOrigin, NM_TO_M, toScene, type AircraftState, type AirportData, type GeoOrigin, type LatLon, type RealismOptions } from '@atc/core';

/** Line styles for map overlays: the procedure in use, its transitions, other procedures, an aircraft's route ahead, a missed approach. */
export type OverlayLineStyle = 'active' | 'transition' | 'dim' | 'route' | 'missed';

/** Extra map drawing keyed by id: procedures in use, the briefing's selection, a route ahead. */
export interface ScopeOverlay {
  lines: { points: LatLon[]; style: OverlayLineStyle }[];
  /** Text beside a point, one entry per line (e.g. "≥060 ≤080"); `marker` draws a ring for points that are not published fixes. */
  labels: { at: LatLon; lines: string[]; style?: OverlayLineStyle; marker?: boolean }[];
  /** Redrawn every frame instead of cached with the map; for overlays that follow an aircraft. */
  live?: boolean;
}

export interface RadarScopeOptions {
  realism: RealismOptions;
  /** Nautical miles from centre to the top edge at start. Default 30. */
  rangeNm?: number;
  showScale?: boolean;
  /** Which runway ends get an extended centreline. Default: ILS ends, else the arrival runway. */
  centrelineEnds?: string[];
}

export interface RadarScope {
  update(states: AircraftState[], simTimeS: number): void;
  setRealism(opts: RealismOptions): void;
  setSelected(callsign: string | null): void;
  getSelected(): string | null;
  /** Where an aircraft's blip is drawn, in CSS pixels from the scope's top-left; null when it is not on the scope. */
  screenPosition(callsign: string): { x: number; y: number } | null;
  toggleScale(): boolean;
  setAssists(enabled: boolean): void;
  resetView(): void;
  /** Draw extra lat/lon polylines over the map (a procedure from the briefing); null clears. Same as overlay "briefing". */
  setHighlight(lines: { lat: number; lon: number }[][] | null): void;
  /** Add, replace or (null) remove a map overlay. */
  setOverlay(id: string, overlay: ScopeOverlay | null): void;
  overlayIds(): string[];
  /** Show the selected aircraft's flown track with its altitude once a minute. */
  setTrail(on: boolean): void;
  on(event: 'select', handler: (callsign: string | null) => void): () => void;
  destroy(): void;
}

interface Track {
  state: AircraftState;
  /** Positions shown (lag behind at high realism because of the sweep). */
  shown: { lat: number; lon: number; altitudeFt: number; headingDeg: number; groundspeedKt: number };
  history: { lat: number; lon: number }[];
  lastHistoryS: number;
  /** Up to 30 minutes of flown positions, drawn for the selected aircraft when the trail is on. */
  trail: { lat: number; lon: number; altitudeFt: number; t: number }[];
  lastTrailS: number;
  lastSweepS: number;
  altHistory: { t: number; alt: number }[];
  sx: number; sy: number;
}

const COLORS = {
  bg: '#05080c', ring: 'rgba(120,140,160,0.18)', ringText: 'rgba(120,140,160,0.5)', map: 'rgba(90,110,130,0.35)', airspace: 'rgba(80,160,220,0.35)',
  runway: '#e8edf2', centreline: 'rgba(200,210,220,0.45)', fix: 'rgba(140,190,140,0.8)', fixText: 'rgba(160,200,160,0.9)',
  arrival: '#f0dca0', departure: '#a8d4ff', ground: '#8a94a0', vector: 'rgba(220,230,240,0.7)', history: 'rgba(120,200,120,0.6)',
  conflict: '#ff4d4d', warning: '#ffb347', waiting: '#5cc8ff', selected: '#ffffff', scale: 'rgba(200,210,220,0.6)', trail: 'rgba(150,215,150,0.6)', trailText: 'rgba(170,225,170,0.85)',
};

const OVERLAY_STYLE: Record<OverlayLineStyle, { stroke: string; width: number; dash: number[]; text: string }> = {
  active: { stroke: 'rgba(92,200,255,0.8)', width: 2, dash: [], text: 'rgba(120,210,255,0.95)' },
  transition: { stroke: 'rgba(92,200,255,0.45)', width: 1.2, dash: [6, 5], text: 'rgba(120,210,255,0.7)' },
  dim: { stroke: 'rgba(140,160,180,0.35)', width: 1, dash: [2, 4], text: 'rgba(160,175,190,0.6)' },
  route: { stroke: 'rgba(240,220,160,0.75)', width: 1.2, dash: [5, 4], text: 'rgba(240,220,160,0.9)' },
  missed: { stroke: 'rgba(92,200,255,0.45)', width: 1, dash: [3, 4], text: 'rgba(120,210,255,0.7)' },
};
/** Dim lines first so the procedure in use stays on top. */
const OVERLAY_ORDER: OverlayLineStyle[] = ['dim', 'missed', 'transition', 'route', 'active'];
/** Trail samples: one every 4 s, 30 minutes kept. */
const TRAIL_EVERY_S = 4, TRAIL_POINTS = 450;
/** Overlay labels are hidden when zoomed out further than this. */
const LABEL_MIN_PX_PER_NM = 6;

export function createRadarScope(container: HTMLElement, airport: AirportData, options: RadarScopeOptions): RadarScope {
  let realism = options.realism;
  const origin: GeoOrigin = createOrigin(airport.lat, airport.lon, airport.elevationM);
  const canvas = document.createElement('canvas');
  canvas.className = 'rs-canvas';
  container.classList.add('rs-container');
  container.appendChild(canvas);
  const ctx = canvas.getContext('2d')!;
  const staticCanvas = document.createElement('canvas');
  const sctx = staticCanvas.getContext('2d')!;

  let w = 1, h = 1, dpr = 1;
  let pxPerNm = 10, panX = 0, panY = 0;
  let staticDirty = true;
  let showScale = options.showScale ?? false;
  let selected: string | null = null;
  let assists = false;
  /** Overlays pre-projected to nm, in insertion order. */
  type Projected = { lines: { pts: { x: number; y: number }[]; style: OverlayLineStyle }[]; labels: { x: number; y: number; lines: string[]; style: OverlayLineStyle; marker: boolean }[]; live: boolean };
  const overlays = new Map<string, Projected>();
  let trailOn = false;
  const tracks = new Map<string, Track>();
  const selectHandlers = new Set<(cs: string | null) => void>();
  let simTime = 0;
  let raf = 0;
  let destroyed = false;

  // Pre-project static geometry to nm (x east, y north).
  const nm = (lat: number, lon: number) => { const p = toScene(origin, lat, lon, 0); return { x: p.x / NM_TO_M, y: -p.z / NM_TO_M }; };
  const fixes = [...airport.fixes.values()].filter((f) => !f.hidden).map((f) => ({ name: f.name, ...nm(f.lat, f.lon), vor: f.name.length === 3 }));
  const runways = airport.runways.map((r) => ({ name: r.name, a: nm(r.ends[0].lat, r.ends[0].lon), b: nm(r.ends[1].lat, r.ends[1].lon), ends: r.ends }));
  const mapSegs = airport.maps.filter((m) => airport.defaultMaps.includes(m.name)).map((m) => {
    const s = m.segments, out = new Float32Array(s.length);
    for (let i = 0; i < s.length; i += 4) { const a = nm(s[i]!, s[i + 1]!), b = nm(s[i + 2]!, s[i + 3]!); out[i] = a.x; out[i + 1] = a.y; out[i + 2] = b.x; out[i + 3] = b.y; }
    return out;
  });
  const airspace = airport.airspace.map((v) => v.poly.map((p) => nm(p.lat, p.lon)));
  const ringCentre = nm(airport.rangeRings.center.lat, airport.rangeRings.center.lon);
  // Extended centrelines: the arrival runway and its parallels (same number), unless the host says otherwise.
  const centrelineEnds = options.centrelineEnds ?? airport.runways.flatMap((r) => r.ends).filter((e) => e.name.replace(/[LRC]$/, '') === airport.arrivalRunway.replace(/[LRC]$/, '')).map((e) => e.name);

  const toScreen = (x: number, y: number) => ({ sx: w / 2 + panX + x * pxPerNm, sy: h / 2 + panY - y * pxPerNm });

  function resize(): void {
    dpr = Math.min(devicePixelRatio || 1, 2);
    w = container.clientWidth || 1; h = container.clientHeight || 1;
    for (const c of [canvas, staticCanvas]) { c.width = Math.round(w * dpr); c.height = Math.round(h * dpr); }
    canvas.style.width = `${w}px`; canvas.style.height = `${h}px`;
    staticDirty = true;
  }
  function resetView(): void {
    const range = options.rangeNm ?? 30;
    pxPerNm = (h / 2) / range; panX = 0; panY = 0; staticDirty = true;
  }
  const ro = new ResizeObserver(() => { const first = pxPerNm === 10; resize(); if (first) resetView(); });
  ro.observe(container);
  resize(); resetView();

  function drawOverlay(c: CanvasRenderingContext2D, ov: Projected): void {
    for (const style of OVERLAY_ORDER) {
      const st = OVERLAY_STYLE[style];
      c.strokeStyle = st.stroke; c.lineWidth = st.width; c.setLineDash(st.dash);
      for (const line of ov.lines) {
        if (line.style !== style || line.pts.length < 2) continue;
        c.beginPath(); line.pts.forEach((p, i) => { const q = toScreen(p.x, p.y); i ? c.lineTo(q.sx, q.sy) : c.moveTo(q.sx, q.sy); }); c.stroke();
      }
    }
    c.setLineDash([]);
    if (pxPerNm < LABEL_MIN_PX_PER_NM) return;
    c.font = '10px ui-monospace, Menlo, monospace';
    for (const l of ov.labels) {
      const s = toScreen(l.x, l.y);
      if (s.sx < -60 || s.sx > w + 60 || s.sy < -40 || s.sy > h + 40) continue;
      const st = OVERLAY_STYLE[l.style];
      if (l.marker) { c.strokeStyle = st.stroke; c.lineWidth = 1; c.beginPath(); c.arc(s.sx, s.sy, 3, 0, Math.PI * 2); c.stroke(); }
      c.fillStyle = st.text;
      // Below the fix name, which sits level with the symbol.
      l.lines.forEach((text, i) => c.fillText(text, s.sx + 6, s.sy + 14 + i * 11));
    }
  }

  // --- Static layer ---
  function drawStatic(): void {
    const c = sctx;
    c.setTransform(dpr, 0, 0, dpr, 0, 0);
    c.fillStyle = COLORS.bg; c.fillRect(0, 0, w, h);
    c.font = '10px ui-monospace, Menlo, monospace';
    // Range rings every 5 nm out to the visible extent.
    const rc = toScreen(ringCentre.x, ringCentre.y);
    const maxNm = Math.hypot(w, h) / pxPerNm;
    c.strokeStyle = COLORS.ring; c.lineWidth = 1; c.fillStyle = COLORS.ringText;
    for (let r = 5; r <= maxNm; r += 5) { c.beginPath(); c.arc(rc.sx, rc.sy, r * pxPerNm, 0, Math.PI * 2); c.stroke(); c.fillText(`${r}`, rc.sx + 3, rc.sy - r * pxPerNm - 2); }
    // Airspace and video map (mid/high).
    if (realism.showVideoMap) {
      c.strokeStyle = COLORS.airspace; c.lineWidth = 1;
      for (const poly of airspace) { c.beginPath(); poly.forEach((p, i) => { const s = toScreen(p.x, p.y); i ? c.lineTo(s.sx, s.sy) : c.moveTo(s.sx, s.sy); }); c.closePath(); c.stroke(); }
      c.strokeStyle = COLORS.map; c.lineWidth = 0.8; c.beginPath();
      for (const segs of mapSegs) for (let i = 0; i < segs.length; i += 4) {
        const a = toScreen(segs[i]!, segs[i + 1]!), b = toScreen(segs[i + 2]!, segs[i + 3]!);
        if ((a.sx < -10 && b.sx < -10) || (a.sx > w + 10 && b.sx > w + 10) || (a.sy < -10 && b.sy < -10) || (a.sy > h + 10 && b.sy > h + 10)) continue;
        c.moveTo(a.sx, a.sy); c.lineTo(b.sx, b.sy);
      }
      c.stroke();
    }
    // Fixes.
    c.fillStyle = COLORS.fixText; c.strokeStyle = COLORS.fix; c.lineWidth = 1;
    for (const f of fixes) {
      const s = toScreen(f.x, f.y);
      if (s.sx < -40 || s.sx > w + 40 || s.sy < -20 || s.sy > h + 20) continue;
      c.beginPath();
      if (f.vor) { for (let k = 0; k < 6; k++) { const a = (k / 6) * Math.PI * 2; const px = s.sx + 4 * Math.cos(a), py = s.sy + 4 * Math.sin(a); k ? c.lineTo(px, py) : c.moveTo(px, py); } c.closePath(); }
      else { c.moveTo(s.sx, s.sy - 4); c.lineTo(s.sx + 4, s.sy + 3); c.lineTo(s.sx - 4, s.sy + 3); c.closePath(); }
      c.stroke();
      if (pxPerNm > 2.2 || f.vor) c.fillText(f.name, s.sx + 6, s.sy + 3);
    }
    // Procedure overlays; live ones are drawn with the aircraft.
    for (const ov of overlays.values()) if (!ov.live) drawOverlay(c, ov);
    // Runways: enlarged about their centre by realism.runwayDrawScale; extended centrelines.
    c.strokeStyle = COLORS.centreline; c.lineWidth = 1; c.setLineDash([4, 6]);
    for (const r of runways) for (const e of r.ends) {
      if (!centrelineEnds.includes(e.name)) continue;
      const far = r.ends[0] === e ? r.ends[1] : r.ends[0];
      const thr = nm(e.lat, e.lon), other = nm(far.lat, far.lon);
      const dx = thr.x - other.x, dy = thr.y - other.y, len = Math.hypot(dx, dy) || 1;
      const ux = dx / len, uy = dy / len;
      const s0 = toScreen(thr.x, thr.y), s1 = toScreen(thr.x + ux * 10, thr.y + uy * 10);
      c.beginPath(); c.moveTo(s0.sx, s0.sy); c.lineTo(s1.sx, s1.sy); c.stroke();
      // Mile ticks along the centreline.
      for (let m = 1; m <= 10; m++) { const t = toScreen(thr.x + ux * m, thr.y + uy * m); c.beginPath(); c.arc(t.sx, t.sy, 1.2, 0, Math.PI * 2); c.stroke(); }
    }
    c.setLineDash([]);
    c.strokeStyle = COLORS.runway; c.lineWidth = Math.max(2, Math.min(6, pxPerNm * 0.12)); c.fillStyle = COLORS.runway; c.font = 'bold 10px ui-monospace, Menlo, monospace';
    for (const r of runways) {
      const cx = (r.a.x + r.b.x) / 2, cy = (r.a.y + r.b.y) / 2, k = realism.runwayDrawScale;
      const a = { x: cx + (r.a.x - cx) * k, y: cy + (r.a.y - cy) * k }, b = { x: cx + (r.b.x - cx) * k, y: cy + (r.b.y - cy) * k };
      const sa = toScreen(a.x, a.y), sb = toScreen(b.x, b.y);
      c.beginPath(); c.moveTo(sa.sx, sa.sy); c.lineTo(sb.sx, sb.sy); c.stroke();
      const ux = (sa.sx - sb.sx) / (Math.hypot(sa.sx - sb.sx, sa.sy - sb.sy) || 1), uy = (sa.sy - sb.sy) / (Math.hypot(sa.sx - sb.sx, sa.sy - sb.sy) || 1);
      c.textAlign = 'center';
      c.fillText(r.ends[0].name, sa.sx + ux * 10, sa.sy + uy * 10 + 3);
      c.fillText(r.ends[1].name, sb.sx - ux * 10, sb.sy - uy * 10 + 3);
      c.textAlign = 'start';
    }
    // Scale markers along the edges (SCALE).
    if (showScale) {
      c.strokeStyle = COLORS.scale; c.fillStyle = COLORS.scale; c.lineWidth = 1; c.font = '9px ui-monospace, Menlo, monospace';
      const c0 = toScreen(0, 0);
      for (let m = -200; m <= 200; m += 5) {
        const x = c0.sx + m * pxPerNm, y = c0.sy - m * pxPerNm;
        if (x >= 0 && x <= w) { c.beginPath(); c.moveTo(x, h - (m % 10 ? 5 : 10)); c.lineTo(x, h); c.stroke(); if (m % 10 === 0) c.fillText(`${Math.abs(m)}`, x + 2, h - 12); }
        if (y >= 0 && y <= h) { c.beginPath(); c.moveTo(0, y); c.lineTo(m % 10 ? 5 : 10, y); c.stroke(); if (m % 10 === 0) c.fillText(`${Math.abs(m)}`, 12, y + 3); }
      }
    }
    staticDirty = false;
  }

  // --- Aircraft layer ---
  function drawAircraft(): void {
    const c = ctx;
    c.setTransform(dpr, 0, 0, dpr, 0, 0);
    c.clearRect(0, 0, w, h);
    c.drawImage(staticCanvas, 0, 0, staticCanvas.width, staticCanvas.height, 0, 0, w, h);
    for (const ov of overlays.values()) if (ov.live) drawOverlay(c, ov);
    c.font = '11px ui-monospace, Menlo, monospace';
    const blink = Math.floor(performance.now() / 400) % 2 === 0;
    // Aircraft on the ground share the airport pixel; stack their blocks so the callsigns stay legible.
    let groundRow = 0;
    for (const t of [...tracks.values()].sort((a, b) => a.state.callsign.localeCompare(b.state.callsign))) {
      const s = t.state, p = t.shown;
      const q = nm(p.lat, p.lon), sc = toScreen(q.x, q.y);
      t.sx = sc.sx; t.sy = sc.sy;
      const onGround = s.onGround;
      const color = s.conflict && blink ? COLORS.conflict : s.conflictWarning && !onGround ? COLORS.warning : onGround ? COLORS.ground : s.kind === 'departure' ? COLORS.departure : COLORS.arrival;
      if (assists && s.callsign === selected && !onGround) {
        c.strokeStyle = 'rgba(122,181,231,0.25)'; c.lineWidth = 1; c.setLineDash([4, 5]);
        c.beginPath(); c.arc(sc.sx, sc.sy, 3 * pxPerNm, 0, Math.PI * 2); c.stroke(); c.setLineDash([]);
        c.fillStyle = COLORS.selected; c.fillText('3 nm spacing', sc.sx + 5, sc.sy + 3 * pxPerNm);
      }
      // Flown track of the selected aircraft, with its altitude (hundreds of feet) once a minute.
      if (trailOn && s.callsign === selected && t.trail.length > 1) {
        c.strokeStyle = COLORS.trail; c.lineWidth = 1.2; c.beginPath();
        t.trail.forEach((tp, i) => { const q = nm(tp.lat, tp.lon), ts = toScreen(q.x, q.y); i ? c.lineTo(ts.sx, ts.sy) : c.moveTo(ts.sx, ts.sy); });
        c.stroke();
        c.font = '9px ui-monospace, Menlo, monospace'; c.fillStyle = COLORS.trailText;
        let lastMark = -Infinity;
        for (const tp of t.trail) {
          if (tp.t - lastMark < 60) continue;
          lastMark = tp.t;
          const q = nm(tp.lat, tp.lon), ts = toScreen(q.x, q.y);
          c.fillRect(ts.sx - 1.5, ts.sy - 1.5, 3, 3);
          c.fillText(String(Math.round(tp.altitudeFt / 100)).padStart(3, '0'), ts.sx + 4, ts.sy - 3);
        }
        c.font = '11px ui-monospace, Menlo, monospace';
      }
      // History dots.
      c.fillStyle = COLORS.history;
      for (const hp of t.history) { const hq = nm(hp.lat, hp.lon), hs = toScreen(hq.x, hq.y); c.fillRect(hs.sx - 1, hs.sy - 1, 2, 2); }
      // Blip.
      c.strokeStyle = color; c.lineWidth = 1.2;
      c.strokeRect(sc.sx - 3.5, sc.sy - 3.5, 7, 7);
      // One-minute vector.
      if (!onGround) {
        const lenNm = p.groundspeedKt / 60;
        const hr = (p.headingDeg * Math.PI) / 180;
        c.beginPath(); c.moveTo(sc.sx, sc.sy); c.lineTo(sc.sx + Math.sin(hr) * lenNm * pxPerNm, sc.sy - Math.cos(hr) * lenNm * pxPerNm); c.strokeStyle = COLORS.vector; c.stroke();
      }
      // Data block: callsign / altitude (hundreds) with trend arrow / speed.
      const altShown = realism.altitudeReadoutLagS > 0 ? laggedAlt(t) : p.altitudeFt;
      const trend = s.verticalSpeedFpm > 150 ? '↑' : s.verticalSpeedFpm < -150 ? '↓' : ' ';
      const line2 = `${String(Math.round(altShown / 100)).padStart(3, '0')}${trend} ${String(Math.round(p.groundspeedKt)).padStart(3, ' ')}`;
      const bx = sc.sx + 14, by = onGround ? sc.sy + 16 + 13 * groundRow++ : sc.sy - 14;
      c.strokeStyle = color; c.lineWidth = 0.8; c.beginPath(); c.moveTo(sc.sx + 4, sc.sy - 4); c.lineTo(bx - 2, by + 4); c.stroke();
      // A pilot waiting on the controller: need code and time since the call, cyan, then amber after a second call, flashing red once pressing.
      const w = s.waiting;
      // An emergency outranks a waiting pilot on the third line: MAYDAY flashes, PAN and minimum fuel are amber.
      const emerg = s.emergency ? `${s.emergency.level === 'mayday' ? 'MAYDAY' : 'PAN'} ${s.emergency.code}` : s.fuelState === 'mayday' ? 'MAYDAY FUEL' : s.fuelState === 'minimum' ? 'MIN FUEL' : '';
      const distress = !!emerg && (s.emergency?.level === 'mayday' || s.fuelState === 'mayday');
      const waitText = emerg || (w ? `${w.need} ${fmtWait(simTime - w.sinceS)}` : '');
      const waitColor = emerg
        ? (distress ? (blink ? COLORS.conflict : 'rgba(255,77,77,0.35)') : COLORS.warning)
        : !w ? color : w.stage >= 2 ? (blink ? COLORS.conflict : 'rgba(255,77,77,0.35)') : w.stage === 1 ? COLORS.warning : COLORS.waiting;
      if (emerg || (w && w.stage >= 2)) { c.strokeStyle = waitColor; c.lineWidth = 1.5; c.beginPath(); c.arc(sc.sx, sc.sy, 10, 0, Math.PI * 2); c.stroke(); }
      if (s.callsign === selected) { const bh = onGround ? 14 : waitText ? 38 : 26, bw = onGround ? (waitText ? 150 : 92) : 62; c.fillStyle = 'rgba(255,255,255,0.12)'; c.fillRect(bx - 3, by - 11, bw, bh); c.strokeStyle = COLORS.selected; c.strokeRect(bx - 3, by - 11, bw, bh); }
      c.fillStyle = color;
      if (onGround) {
        const label = `${s.callsign} ${s.type}`;
        c.fillText(label, bx, by);
        if (waitText) { c.fillStyle = waitColor; c.fillText(waitText, bx + c.measureText(label).width + 8, by); }
        continue;
      }
      c.fillText(s.callsign, bx, by);
      c.fillText(line2, bx, by + 12);
      if (waitText) { c.fillStyle = waitColor; c.fillText(waitText, bx, by + 24); }
    }
  }

  const fmtWait = (sec: number) => { const v = Math.max(0, Math.floor(sec)); return `${Math.floor(v / 60)}:${String(v % 60).padStart(2, '0')}`; };

  function laggedAlt(t: Track): number {
    const cutoff = simTime - realism.altitudeReadoutLagS;
    let alt = t.altHistory[0]?.alt ?? t.shown.altitudeFt;
    for (const e of t.altHistory) { if (e.t <= cutoff) alt = e.alt; else break; }
    return alt;
  }

  function frame(): void {
    if (destroyed) return;
    raf = requestAnimationFrame(frame);
    if (staticDirty) drawStatic();
    drawAircraft();
  }
  raf = requestAnimationFrame(frame);

  // --- Interaction: drag to pan, wheel to zoom, click to select, double-click resets. ---
  let drag: { x: number; y: number; px: number; py: number; moved: boolean } | null = null;
  canvas.addEventListener('pointerdown', (e) => { drag = { x: e.clientX, y: e.clientY, px: panX, py: panY, moved: false }; canvas.setPointerCapture(e.pointerId); });
  canvas.addEventListener('pointermove', (e) => {
    if (!drag) return;
    const dx = e.clientX - drag.x, dy = e.clientY - drag.y;
    if (Math.hypot(dx, dy) > 4) drag.moved = true;
    if (drag.moved) { panX = drag.px + dx; panY = drag.py + dy; staticDirty = true; }
  });
  canvas.addEventListener('pointerup', (e) => {
    if (!drag) return;
    const moved = drag.moved; drag = null;
    if (moved) return;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left, y = e.clientY - rect.top;
    let best: Track | null = null, bd = 18;
    for (const t of tracks.values()) {
      const d = Math.min(Math.hypot(t.sx - x, t.sy - y), Math.hypot(t.sx + 40 - x, t.sy - 8 - y));
      if (d < bd) { bd = d; best = t; }
    }
    api.setSelected(best?.state.callsign ?? null);
  });
  canvas.addEventListener('wheel', (e) => {
    e.preventDefault();
    const rect = canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left - w / 2, my = e.clientY - rect.top - h / 2;
    const f = Math.exp(-e.deltaY * 0.0015);
    const next = Math.max(1.5, Math.min(120, pxPerNm * f));
    const k = next / pxPerNm;
    panX = mx - (mx - panX) * k; panY = my - (my - panY) * k; pxPerNm = next; staticDirty = true;
  }, { passive: false });
  canvas.addEventListener('dblclick', () => resetView());

  const api: RadarScope = {
    update(states, simTimeS) {
      simTime = simTimeS;
      const seen = new Set<string>();
      for (const s of states) {
        seen.add(s.callsign);
        let t = tracks.get(s.callsign);
        if (!t) { t = { state: s, shown: { lat: s.lat, lon: s.lon, altitudeFt: s.altitudeFt, headingDeg: s.headingDeg, groundspeedKt: s.groundspeedKt }, history: [], lastHistoryS: simTimeS, trail: [], lastTrailS: -Infinity, lastSweepS: -Infinity, altHistory: [], sx: 0, sy: 0 }; tracks.set(s.callsign, t); }
        t.state = s;
        if (realism.radarSweepS <= 0 || simTimeS - t.lastSweepS >= realism.radarSweepS) {
          t.lastSweepS = simTimeS;
          t.shown = { lat: s.lat, lon: s.lon, altitudeFt: s.altitudeFt, headingDeg: s.headingDeg, groundspeedKt: s.groundspeedKt };
        }
        t.altHistory.push({ t: simTimeS, alt: s.altitudeFt });
        while (t.altHistory.length > 0 && t.altHistory[0]!.t < simTimeS - 12) t.altHistory.shift();
        if (!s.onGround && simTimeS - t.lastHistoryS >= 4) { t.lastHistoryS = simTimeS; t.history.push({ lat: s.lat, lon: s.lon }); if (t.history.length > 6) t.history.shift(); }
        if (!s.onGround && simTimeS - t.lastTrailS >= TRAIL_EVERY_S) { t.lastTrailS = simTimeS; t.trail.push({ lat: s.lat, lon: s.lon, altitudeFt: s.altitudeFt, t: simTimeS }); if (t.trail.length > TRAIL_POINTS) t.trail.shift(); }
      }
      for (const cs of tracks.keys()) if (!seen.has(cs)) { tracks.delete(cs); if (selected === cs) api.setSelected(null); }
    },
    setRealism(opts) { realism = opts; staticDirty = true; },
    setSelected(cs) { if (cs === selected) return; selected = cs; for (const hd of selectHandlers) hd(cs); },
    getSelected() { return selected; },
    screenPosition(cs) { const t = tracks.get(cs); return t && w > 1 && h > 1 ? { x: t.sx, y: t.sy } : null; },
    setAssists(enabled) { assists = enabled; },
    toggleScale() { showScale = !showScale; staticDirty = true; return showScale; },
    resetView,
    setHighlight(lines) { api.setOverlay('briefing', lines ? { lines: lines.map((points) => ({ points, style: 'active' })), labels: [] } : null); },
    setOverlay(id, overlay) {
      const wasStatic = overlays.has(id) && !overlays.get(id)!.live;
      if (!overlay) overlays.delete(id);
      else overlays.set(id, {
        lines: overlay.lines.map((l) => ({ pts: l.points.map((p) => nm(p.lat, p.lon)), style: l.style })),
        labels: overlay.labels.map((l) => ({ ...nm(l.at.lat, l.at.lon), lines: l.lines, style: l.style ?? 'active', marker: !!l.marker })),
        live: !!overlay.live,
      });
      if (wasStatic || (overlay && !overlay.live)) staticDirty = true;
    },
    overlayIds() { return [...overlays.keys()]; },
    setTrail(on) { trailOn = on; },
    on(_event, handler) { selectHandlers.add(handler); return () => selectHandlers.delete(handler); },
    destroy() { destroyed = true; cancelAnimationFrame(raf); ro.disconnect(); canvas.remove(); container.classList.remove('rs-container'); },
  };
  return api;
}
